import {
  BulkConversationActionJobDocument,
  BulkConversationActionJobQuery,
  BulkConversationActionJobQueryVariables,
  BulkConversationCloseFilterInput,
  StartBulkConversationCloseDocument,
  StartBulkConversationCloseMutation,
  StartBulkConversationCloseMutationVariables,
} from '@/generated/graphql';
import {ApolloClient} from '@apollo/client';

export type BulkJobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

export type BulkCloseRun = {
  jobId: string;
  status: BulkJobStatus;
  total: number;
  processed: number;
  succeeded: number;
  skipped: number;
  failed: number;
  startedAt: string | null;
  finishedAt: string | null;
};

export type BulkCloseCounts = {
  total: number;
  done: number;
  succeeded: number;
  skipped: number;
  failed: number;
  isComplete: boolean;
};

type JobShape = {
  id: string;
  status?: string | null;
  total: number;
  processed: number;
  succeeded: number;
  skipped: number;
  failed: number;
  startedAt?: string | null;
  finishedAt?: string | null;
};

export type BulkCloseAction = 'close_silent' | 'close_message';

type BulkCloseStartBaseInput = {
  conversationIds?: string[];
  filters?: BulkConversationCloseFilterInput;
  closedReason: string;
  consent: boolean;
};

export type BulkCloseStartInput =
  | (BulkCloseStartBaseInput & {
      action?: 'close_silent';
      message?: never;
      notify?: never;
    })
  | (BulkCloseStartBaseInput & {
      action: 'close_message';
      message: string;
      notify: boolean;
    });

const STORAGE_KEY = 'conversations.bulkClose.v1';
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_FAILURES = 5;
const MAX_POLL_BACKOFF_MS = 30000;

type Listener = () => void;

const listeners = new Set<Listener>();

let run: BulkCloseRun | null = null;
let client: ApolloClient<object> | null = null;
let onComplete: (() => void) | null = null;
let onError: ((message: string) => void) | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollFailureCount = 0;
let storageFailureNotified = false;

const notify = () => listeners.forEach(listener => listener());

const persist = () => {
  try {
    if (run) localStorage.setItem(STORAGE_KEY, JSON.stringify(run));
    else localStorage.removeItem(STORAGE_KEY);
    storageFailureNotified = false;
  } catch {
    if (!storageFailureNotified) {
      onError?.('Could not save close progress for reload recovery.');
      storageFailureNotified = true;
    }
  }
};

const isBulkJobStatus = (status: unknown): status is BulkJobStatus =>
  status === 'PENDING' ||
  status === 'PROCESSING' ||
  status === 'COMPLETED' ||
  status === 'FAILED';

const isTerminal = (status: BulkJobStatus) =>
  status === 'COMPLETED' || status === 'FAILED';

const normalizeJobStatus = (status: unknown): BulkJobStatus =>
  isBulkJobStatus(status) ? status : 'PENDING';

const applyJob = (job: JobShape) => {
  run = {
    jobId: job.id,
    status: normalizeJobStatus(job.status),
    total: job.total,
    processed: job.processed,
    succeeded: job.succeeded,
    skipped: job.skipped,
    failed: job.failed,
    startedAt: job.startedAt ?? null,
    finishedAt: job.finishedAt ?? null,
  };
  persist();
  notify();
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeDateString = (value: unknown) => {
  if (typeof value !== 'string') return null;
  if (Number.isNaN(Date.parse(value))) return null;
  return value;
};

const normalizeStoredRun = (storedRun: unknown): BulkCloseRun | null => {
  if (!storedRun || typeof storedRun !== 'object') return null;

  const candidate = storedRun as Partial<BulkCloseRun>;
  if (typeof candidate.jobId !== 'string' || candidate.jobId.length === 0) {
    return null;
  }
  if (!isBulkJobStatus(candidate.status)) return null;
  if (
    !isFiniteNumber(candidate.total) ||
    !isFiniteNumber(candidate.processed) ||
    !isFiniteNumber(candidate.succeeded) ||
    !isFiniteNumber(candidate.skipped) ||
    !isFiniteNumber(candidate.failed)
  ) {
    return null;
  }

  return {
    jobId: candidate.jobId,
    status: candidate.status,
    total: candidate.total,
    processed: candidate.processed,
    succeeded: candidate.succeeded,
    skipped: candidate.skipped,
    failed: candidate.failed,
    startedAt: normalizeDateString(candidate.startedAt),
    finishedAt: normalizeDateString(candidate.finishedAt),
  };
};

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const schedulePoll = (delayMs = POLL_INTERVAL_MS) => {
  stopPolling();
  pollTimer = setTimeout(poll, delayMs);
};

const failPolling = () => {
  stopPolling();
  run = null;
  persist();
  notify();
  onError?.('Could not check close progress. Please refresh and try again.');
};

const getPollBackoff = () =>
  Math.min(POLL_INTERVAL_MS * 2 ** pollFailureCount, MAX_POLL_BACKOFF_MS);

const shouldRetryPoll = (error: unknown) => {
  const graphQLErrors = (error as {graphQLErrors?: unknown})?.graphQLErrors;
  return !(Array.isArray(graphQLErrors) && graphQLErrors.length > 0);
};

const poll = async () => {
  if (!client || !run) return;
  const jobId = run.jobId;
  try {
    const {data} = await client.query<
      BulkConversationActionJobQuery,
      BulkConversationActionJobQueryVariables
    >({
      query: BulkConversationActionJobDocument,
      variables: {id: jobId},
      fetchPolicy: 'no-cache',
    });
    if (!run || run.jobId !== jobId) return;
    const job = data?.bulkConversationActionJob;
    if (!job) {
      stopPolling();
      run = null;
      persist();
      notify();
      onError?.('Close job could not be found. It may have already completed.');
      return;
    }
    pollFailureCount = 0;
    applyJob(job);
    if (run && isTerminal(run.status)) {
      stopPolling();
      onComplete?.();
      return;
    }
  } catch (error) {
    if (!run || run.jobId !== jobId) return;
    pollFailureCount += 1;
    if (pollFailureCount > MAX_POLL_FAILURES || !shouldRetryPoll(error)) {
      failPolling();
      pollFailureCount = 0;
      return;
    }
    schedulePoll(getPollBackoff());
    return;
  }
  schedulePoll();
};

export const bulkCloseRunner = {
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  getSnapshot: (): BulkCloseRun | null => run,

  setClient: (apolloClient: ApolloClient<object>) => {
    client = apolloClient;
  },

  setOnComplete: (fn: () => void) => {
    onComplete = fn;
  },

  setOnError: (fn: (message: string) => void) => {
    onError = fn;
  },

  start: async (input: BulkCloseStartInput) => {
    if (!client) return;
    const hasIds = !!input.conversationIds && input.conversationIds.length > 0;
    if (!hasIds && !input.filters) return;
    if (input.action === 'close_message' && input.message.trim().length === 0) {
      onError?.('Could not start close. A message is required.');
      return;
    }
    try {
      const {data} = await client.mutate<
        StartBulkConversationCloseMutation,
        StartBulkConversationCloseMutationVariables
      >({
        mutation: StartBulkConversationCloseDocument,
        variables: {
          conversationIds: hasIds ? input.conversationIds : undefined,
          filters: hasIds ? undefined : input.filters,
          closedReason: input.closedReason,
          consent: input.consent,
          action: input.action,
          message: input.action === 'close_message' ? input.message : undefined,
          notify: input.action === 'close_message' ? input.notify : undefined,
        },
      });
      const job = data?.startBulkConversationClose?.job;
      if (job) {
        applyJob(job);
        if (run && !isTerminal(run.status)) schedulePoll();
        else onComplete?.();
      }
    } catch {
      onError?.('Could not start close. Please try again.');
    }
  },

  resumeFromStorage: () => {
    if (run) return;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const restoredRun = normalizeStoredRun(JSON.parse(raw));
      if (!restoredRun) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      run = restoredRun;
      notify();
      if (!isTerminal(run.status)) schedulePoll();
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  },

  dismiss: () => {
    stopPolling();
    run = null;
    persist();
    notify();
  },
};

export const getCounts = (current: BulkCloseRun | null): BulkCloseCounts => {
  if (!current) {
    return {
      total: 0,
      done: 0,
      succeeded: 0,
      skipped: 0,
      failed: 0,
      isComplete: true,
    };
  }
  return {
    total: current.total,
    done: current.processed,
    succeeded: current.succeeded,
    skipped: current.skipped,
    failed: current.failed,
    isComplete: isTerminal(current.status),
  };
};
